import {
  ConversationalBtmStreamEvent,
  ConversationalBtmTerminalStatus,
  isConversationalBtmStreamEvent,
} from './conversational-btm-events';
import { ConversationalBtmMessagePart } from './conversational-btm.models';
import { ConversationalBtmSseParser } from './sse-parser';

const MAX_APPLIED_EVENT_IDS = 2_000;
const THINKING_ID = '__thinking__';

export interface ConversationalBtmRunMessage {
  id: string;
  content: string;
  parts: ConversationalBtmMessagePart[];
}

export interface ConversationalBtmRunState {
  runId: string;
  messageOrder: readonly string[];
  messagesById: Readonly<Record<string, ConversationalBtmRunMessage>>;
  pendingParentlessToolOrder: readonly string[];
  pendingParentlessToolPartsById: Readonly<
    Record<string, ConversationalBtmMessagePart>
  >;
  appliedEventIds: readonly string[];
  currentMessageId?: string;
  lastEventId?: string;
  terminal: ConversationalBtmTerminalStatus;
  error?: string;
}

export interface ConversationalBtmStreamResult {
  state: ConversationalBtmRunState;
  terminal: ConversationalBtmTerminalStatus;
}

export function createConversationalBtmRunState(
  runId: string,
  initialMessages: readonly ConversationalBtmRunMessage[] = []
): ConversationalBtmRunState {
  const messagesById = Object.fromEntries(
    initialMessages.map((message) => [message.id, cloneMessage(message)])
  );
  return {
    runId,
    messageOrder: initialMessages.map((message) => message.id),
    messagesById,
    pendingParentlessToolOrder: [],
    pendingParentlessToolPartsById: {},
    appliedEventIds: [],
    currentMessageId: initialMessages.at(-1)?.id,
    terminal: null,
  };
}

export function conversationalBtmRunMessages(
  state: ConversationalBtmRunState
): ConversationalBtmRunMessage[] {
  return state.messageOrder.flatMap((id) => {
    const message = state.messagesById[id];
    return message ? [cloneMessage(message)] : [];
  });
}

export function reduceConversationalBtmRunEvent(
  state: ConversationalBtmRunState,
  event: ConversationalBtmStreamEvent,
  eventId?: string
): ConversationalBtmRunState {
  if (eventId && state.appliedEventIds.includes(eventId)) return state;

  let next = cloneState(state);
  const preferredMessageId = text(event.messageId);

  switch (event.type) {
    case 'TEXT_MESSAGE_START':
      next = ensureMessage(next, preferredMessageId);
      if (preferredMessageId) {
        next = attachPendingParentlessToolParts(next, preferredMessageId);
      }
      break;
    case 'TEXT_MESSAGE_CONTENT':
    case 'TEXT_MESSAGE_CHUNK': {
      const delta = text(event.delta);
      if (delta) {
        next = ensureMessage(next, preferredMessageId);
        const messageId = next.currentMessageId as string;
        if (preferredMessageId) {
          next = attachPendingParentlessToolParts(next, preferredMessageId);
        }
        const message = cloneMessage(next.messagesById[messageId]);
        message.content += delta;
        const lastPart = message.parts.at(-1);
        if (lastPart?.type === 'text' && typeof lastPart['text'] === 'string') {
          message.parts[message.parts.length - 1] = {
            ...lastPart,
            text: `${lastPart['text']}${delta}`,
          };
        } else {
          message.parts.push({ type: 'text', text: delta });
        }
        next = replaceMessage(next, message);
      }
      break;
    }
    case 'REASONING_MESSAGE_START':
    case 'THINKING_TEXT_MESSAGE_START':
      next = appendPart(next, preferredMessageId, {
        type: 'reasoning',
        text: '',
        stream_key: preferredMessageId ?? THINKING_ID,
      });
      break;
    case 'REASONING_MESSAGE_CONTENT':
    case 'REASONING_MESSAGE_CHUNK':
    case 'THINKING_TEXT_MESSAGE_CONTENT': {
      const delta = text(event.delta);
      if (delta) next = appendReasoning(next, preferredMessageId, delta);
      break;
    }
    case 'TOOL_CALL_START':
    case 'TOOL_CALL_CHUNK':
    case 'TOOL_CALL_ARGS':
    case 'TOOL_CALL_END':
    case 'TOOL_CALL_RESULT':
    case 'TOOL_CALL_ERROR':
      next = reduceToolEvent(next, event);
      break;
    case 'CUSTOM':
      next = reduceCustomEvent(next, event);
      break;
    case 'RUN_FINISHED':
      next = { ...next, terminal: 'finished' };
      break;
    case 'RUN_ERROR':
      next = {
        ...next,
        terminal: 'error',
        error: text(event.message) ?? 'Agent run failed.',
      };
      break;
  }

  if (eventId !== undefined) {
    next = {
      ...next,
      lastEventId: eventId || undefined,
      appliedEventIds: eventId
        ? [...next.appliedEventIds, eventId].slice(-MAX_APPLIED_EVENT_IDS)
        : next.appliedEventIds,
    };
  }
  return next;
}

export async function processConversationalBtmRunStream(options: {
  response: Response;
  initialState: ConversationalBtmRunState;
  onSnapshot?: (state: ConversationalBtmRunState) => void;
}): Promise<ConversationalBtmStreamResult> {
  if (!options.response.body) {
    throw new Error('The run stream returned no response body.');
  }

  const reader = options.response.body.getReader();
  const decoder = new TextDecoder();
  const parser = new ConversationalBtmSseParser();
  let state = options.initialState;

  const applyRecords = async (records: ReturnType<ConversationalBtmSseParser['push']>) => {
    for (const record of records) {
      let payload: unknown;
      try {
        payload = JSON.parse(record.data);
      } catch {
        continue;
      }
      if (!isConversationalBtmStreamEvent(payload)) continue;

      state = reduceConversationalBtmRunEvent(state, payload, record.id);
      options.onSnapshot?.(state);
      if (state.terminal !== null) {
        try {
          await reader.cancel();
        } catch {
          // The terminal event remains authoritative when transport cleanup fails.
        }
        return true;
      }
    }
    return false;
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    const records = parser.push(decoder.decode(value, { stream: true }));
    if (await applyRecords(records)) return { state, terminal: state.terminal };
  }

  const tail = decoder.decode();
  if (tail && (await applyRecords(parser.push(tail)))) {
    return { state, terminal: state.terminal };
  }
  await applyRecords(parser.finish());
  return { state, terminal: state.terminal };
}

function ensureMessage(
  state: ConversationalBtmRunState,
  preferredId?: string
): ConversationalBtmRunState {
  const id = preferredId ?? state.currentMessageId ?? `${state.runId}:assistant`;
  if (state.messagesById[id]) return { ...state, currentMessageId: id };
  return {
    ...state,
    currentMessageId: id,
    messageOrder: [...state.messageOrder, id],
    messagesById: {
      ...state.messagesById,
      [id]: { id, content: '', parts: [] },
    },
  };
}

function appendPart(
  state: ConversationalBtmRunState,
  preferredId: string | undefined,
  part: ConversationalBtmMessagePart
): ConversationalBtmRunState {
  const next = ensureMessage(state, preferredId);
  const id = next.currentMessageId as string;
  const message = cloneMessage(next.messagesById[id]);
  message.parts.push({ ...part });
  return replaceMessage(next, message);
}

function appendReasoning(
  state: ConversationalBtmRunState,
  preferredId: string | undefined,
  delta: string
): ConversationalBtmRunState {
  const next = ensureMessage(state, preferredId);
  const id = next.currentMessageId as string;
  const message = cloneMessage(next.messagesById[id]);
  const lastPart = message.parts.at(-1);
  if (lastPart?.type === 'reasoning' && typeof lastPart['text'] === 'string') {
    message.parts[message.parts.length - 1] = {
      ...lastPart,
      text: `${lastPart['text']}${delta}`,
    };
  } else {
    message.parts.push({ type: 'reasoning', text: delta });
  }
  return replaceMessage(next, message);
}

function reduceToolEvent(
  state: ConversationalBtmRunState,
  event: ConversationalBtmStreamEvent
): ConversationalBtmRunState {
  const toolCallId = text(event.toolCallId);
  if (!toolCallId) return state;
  const parentMessageId = text(event.parentMessageId);
  const currentMessageId =
    state.currentMessageId === `${state.runId}:assistant`
      ? undefined
      : state.currentMessageId;
  const preferredId = parentMessageId ?? currentMessageId;
  if (!preferredId) {
    return reducePendingParentlessToolEvent(state, event, toolCallId);
  }
  const next = ensureMessage(state, preferredId);
  const id = next.currentMessageId as string;
  const message = cloneMessage(next.messagesById[id]);
  const index = message.parts.findIndex(
    (part) => part.type === 'tool' && part['toolCallId'] === toolCallId
  );
  const current =
    (index >= 0 ? message.parts[index] : undefined) ??
    next.pendingParentlessToolPartsById[toolCallId];
  const part = reduceToolPart(current, event, toolCallId);
  if (index >= 0) message.parts[index] = part;
  else message.parts.push(part);
  return removePendingParentlessToolPart(
    replaceMessage(next, message),
    toolCallId
  );
}

function reducePendingParentlessToolEvent(
  state: ConversationalBtmRunState,
  event: ConversationalBtmStreamEvent,
  toolCallId: string
): ConversationalBtmRunState {
  const current = state.pendingParentlessToolPartsById[toolCallId];
  return {
    ...state,
    pendingParentlessToolOrder: current
      ? state.pendingParentlessToolOrder
      : [...state.pendingParentlessToolOrder, toolCallId],
    pendingParentlessToolPartsById: {
      ...state.pendingParentlessToolPartsById,
      [toolCallId]: reduceToolPart(current, event, toolCallId),
    },
  };
}

function reduceToolPart(
  current: ConversationalBtmMessagePart | undefined,
  event: ConversationalBtmStreamEvent,
  toolCallId: string
): ConversationalBtmMessagePart {
  const part: ConversationalBtmMessagePart = {
    type: 'tool',
    toolCallId,
    toolName: text(event.toolCallName) ?? current?.['toolName'] ?? 'unknown_tool',
    state: current?.['state'] ?? 'input-streaming',
    ...(current ?? {}),
  };
  if (typeof event.delta === 'string') {
    part['rawInput'] = `${String(part['rawInput'] ?? '')}${event.delta}`;
  }
  if (event.type === 'TOOL_CALL_END') part['state'] = 'input-available';
  if (event.type === 'TOOL_CALL_RESULT') {
    part['state'] = 'output-available';
    part['output'] = event.content;
  }
  if (event.type === 'TOOL_CALL_ERROR') {
    part['state'] = 'output-error';
    part['errorText'] = text(event.message) ?? 'Tool call failed.';
  }
  return part;
}

function attachPendingParentlessToolParts(
  state: ConversationalBtmRunState,
  messageId: string
): ConversationalBtmRunState {
  if (state.pendingParentlessToolOrder.length === 0) return state;
  const existing = state.messagesById[messageId];
  if (!existing) return state;

  const message = cloneMessage(existing);
  for (const toolCallId of state.pendingParentlessToolOrder) {
    const pending = state.pendingParentlessToolPartsById[toolCallId];
    if (!pending) continue;
    const index = message.parts.findIndex(
      (part) => part.type === 'tool' && part['toolCallId'] === toolCallId
    );
    if (index >= 0) message.parts[index] = { ...pending };
    else message.parts.push({ ...pending });
  }

  return {
    ...replaceMessage(state, message),
    pendingParentlessToolOrder: [],
    pendingParentlessToolPartsById: {},
  };
}

function removePendingParentlessToolPart(
  state: ConversationalBtmRunState,
  toolCallId: string
): ConversationalBtmRunState {
  if (!state.pendingParentlessToolPartsById[toolCallId]) return state;
  const pendingParentlessToolPartsById = {
    ...state.pendingParentlessToolPartsById,
  };
  delete pendingParentlessToolPartsById[toolCallId];
  return {
    ...state,
    pendingParentlessToolOrder: state.pendingParentlessToolOrder.filter(
      (id) => id !== toolCallId
    ),
    pendingParentlessToolPartsById,
  };
}

function reduceCustomEvent(
  state: ConversationalBtmRunState,
  event: ConversationalBtmStreamEvent
): ConversationalBtmRunState {
  if (event.name === 'sources') {
    const value = record(event.value);
    if (Array.isArray(value?.['sources'])) {
      return appendPart(state, state.currentMessageId, {
        type: 'sources',
        sources: value['sources'],
      });
    }
  }
  return state;
}

function replaceMessage(
  state: ConversationalBtmRunState,
  message: ConversationalBtmRunMessage
): ConversationalBtmRunState {
  return {
    ...state,
    messagesById: { ...state.messagesById, [message.id]: message },
  };
}

function cloneState(
  state: ConversationalBtmRunState
): ConversationalBtmRunState {
  return {
    ...state,
    messageOrder: [...state.messageOrder],
    messagesById: Object.fromEntries(
      Object.entries(state.messagesById).map(([id, message]) => [
        id,
        cloneMessage(message),
      ])
    ),
    pendingParentlessToolOrder: [...state.pendingParentlessToolOrder],
    pendingParentlessToolPartsById: Object.fromEntries(
      Object.entries(state.pendingParentlessToolPartsById).map(([id, part]) => [
        id,
        { ...part },
      ])
    ),
    appliedEventIds: [...state.appliedEventIds],
  };
}

function cloneMessage(
  message: ConversationalBtmRunMessage
): ConversationalBtmRunMessage {
  return {
    ...message,
    parts: message.parts.map((part) => ({ ...part })),
  };
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}
